/**
 * Pure Thai PIT math (CLAUDE.md core invariant: tax logic stays behind TaxPolicy; here it is
 * split into small PURE functions so each is exhaustively unit-testable against the published
 * Revenue Department tables — see test/tax-th.test.ts golden tests). No I/O, no randomness, no
 * dependence on the simulator. Everything is a deterministic function of its inputs + the
 * year-versioned config in ./config.
 */

import type { FundDeductionRule, PitBracket, ThaiTaxYearConfig } from './config';

/**
 * Progressive tax on `netIncome` (income AFTER all allowances/deductions) over the bracket
 * schedule. Each bracket's marginal `rate` applies only to the slice of income inside that band
 * (RD method). Negative/zero net income -> 0. This is the workhorse the golden tests pin.
 */
export function pitOnNetIncome(netIncome: number, brackets: readonly PitBracket[]): number {
  if (netIncome <= 0) {
    return 0;
  }
  let tax = 0;
  for (const b of brackets) {
    if (netIncome <= b.lower) {
      break;
    }
    const top = Math.min(netIncome, b.upper);
    const slice = top - b.lower;
    if (slice > 0) {
      tax += slice * b.rate;
    }
  }
  return tax;
}

/**
 * The marginal rate that applies at `netIncome` — the rate of the band the LAST baht falls in
 * (0 below the first taxable edge). Used for `TaxResult.marginalRate` reporting only.
 */
export function marginalRate(netIncome: number, brackets: readonly PitBracket[]): number {
  if (netIncome <= 0) {
    return 0;
  }
  let rate = 0;
  for (const b of brackets) {
    if (netIncome > b.lower) {
      rate = b.rate;
    } else {
      break;
    }
  }
  return rate;
}

/**
 * The employment-income expense deduction: `min(income * pct, cap)` (RD: 50% capped at THB 100k).
 * Applied to assessable EMPLOYMENT income only; non-employment assessable income gets no expense
 * deduction here (its own rules are out of scope — see index.ts TODO).
 */
export function employmentExpenseDeduction(
  employmentIncome: number,
  cfg: ThaiTaxYearConfig,
): number {
  if (employmentIncome <= 0) {
    return 0;
  }
  return Math.min(
    employmentIncome * cfg.employmentExpenseDeductionPct,
    cfg.employmentExpenseDeductionCap,
  );
}

/**
 * The s.47 bis dividend tax credit (the imputation / gross-up election alternative to the 10%
 * final WHT). A Thai resident may elect to INCLUDE dividends in assessable income, grossing them
 * up by the corporate tax already paid, then credit that grossed-up amount against the PIT due.
 *
 *   credit = dividend * rate / (1 - rate)        [RD-47bis], rate = the payer's CIT rate.
 *
 * The grossed-up income added to the PIT base is `dividend + credit`. The credit (plus any 10% WHT
 * withheld at source) is then subtracted from total PIT. This election BEATS the 10% final tax when
 * the taxpayer's marginal rate is below the corporate rate (low earners get a refund of the excess
 * corporate tax); the final-tax election wins at high marginal rates. Callers (index.ts) pick the
 * cheaper election per the brief's "WHT-final vs credit" note.
 */
export function dividendTaxCredit(dividend: number, corporateTaxRate: number): number {
  if (dividend <= 0 || corporateTaxRate <= 0 || corporateTaxRate >= 1) {
    return 0;
  }
  return (dividend * corporateTaxRate) / (1 - corporateTaxRate);
}

/**
 * The deductible amount actually allowed for ONE fund vehicle given its own rule and the income:
 * `min(contributed, income * pctOfIncome, standaloneCap)`. The THB 500k COMBINED cap is applied
 * separately (see `applyCombinedCap`) across all vehicles that share it.
 */
export function fundDeduction(
  contributed: number,
  income: number,
  rule: FundDeductionRule,
): number {
  if (contributed <= 0 || income <= 0) {
    return 0;
  }
  return Math.min(contributed, income * rule.pctOfIncome, rule.cap);
}

/**
 * Apply the THB 500k COMBINED retirement cap to the set of per-vehicle deductions that share it
 * (RMF + SSF + PVD + ... — [SH-RMF][SH-SSF]). If the sum of the combined-cap deductions exceeds the
 * cap, scale them down proportionally so the total equals the cap; vehicles OUTSIDE the combined
 * cap (Thai ESG/ESGX) pass through untouched. Returns the total allowed deduction across ALL inputs.
 *
 * `entries` is `{ deduction, inCombinedCap }` per vehicle (deduction already through `fundDeduction`).
 */
export function applyCombinedCap(
  entries: readonly { readonly deduction: number; readonly inCombinedCap: boolean }[],
  combinedCap: number,
): number {
  let combinedSum = 0;
  let separateSum = 0;
  for (const e of entries) {
    if (e.inCombinedCap) {
      combinedSum += e.deduction;
    } else {
      separateSum += e.deduction;
    }
  }
  const allowedCombined = Math.min(combinedSum, combinedCap);
  return allowedCombined + separateSum;
}
